import { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Copy, Ban, CheckCircle, Trash2, Edit, RefreshCw, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import {
  useKeyInfo,
  useDeleteKey,
  useBlockKey,
  useRegenerateKey,
  useResetKeySpend,
} from '@/api/hooks/useKeys';
import { ApiException } from '@/api/client';

export function KeyDetailPage() {
  const { key = '' } = useParams<{ key: string }>();
  const decodedKey = decodeURIComponent(key);
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useKeyInfo(decodedKey);
  const deleteKey = useDeleteKey();
  const blockKey = useBlockKey();
  const regenerateKey = useRegenerateKey();
  const resetSpend = useResetKeySpend();
  const [showKey, setShowKey] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [regeneratedKey, setRegeneratedKey] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div
        style={{
          height: 384,
          background: '#e2e8f0',
          borderRadius: 8,
          animation: 'pulse 2s infinite',
        }}
      />
    );
  }

  if (error || !data) {
    return (
      <Card>
        <div style={{ padding: 24, color: '#991b1b', fontSize: 13 }}>
          <strong>Failed to load key:</strong>{' '}
          {error instanceof ApiException ? error.message : 'Unknown error'}
          <div style={{ marginTop: 12 }}>
            <Button variant="outline" onClick={() => navigate('/keys')}>
              Back to Keys
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  const masked = `sk-...${data.key.slice(-8)}`;

  const runAction = (
    name: string,
    fn: () => void,
    confirmMsg?: string,
  ) => {
    if (confirmMsg && !confirm(confirmMsg)) return;
    setActionError(null);
    try {
      fn();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : `${name} failed`);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>
          {data.key_alias ?? masked}
        </h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link to={`/keys/${encodeURIComponent(data.key)}/edit`}>
            <Button variant="outline">
              <Edit size={16} />
              <span>Edit</span>
            </Button>
          </Link>
          <Button
            variant="outline"
            onClick={() =>
              runAction('Block', () =>
                blockKey.mutate(
                  { key: data.key, blocked: !data.blocked },
                  { onSuccess: () => refetch() },
                ),
              )
            }
          >
            {data.blocked ? <CheckCircle size={16} /> : <Ban size={16} />}
            <span>{data.blocked ? 'Unblock' : 'Block'}</span>
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              runAction(
                'Regenerate',
                () => {
                  regenerateKey.mutate(data.key, {
                    onSuccess: (newKey: { key: string }) => {
                      setRegeneratedKey(newKey.key);
                    },
                  });
                },
                'Regenerate this key? Old key will be invalidated.',
              )
            }
          >
            <RefreshCw size={16} />
            <span>Regenerate</span>
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              runAction(
                'Reset',
                () =>
                  resetSpend.mutate(
                    { key: data.key, reset_to: 0 },
                    { onSuccess: () => refetch() },
                  ),
                'Reset spend to $0?',
              )
            }
          >
            <DollarSign size={16} />
            <span>Reset Spend</span>
          </Button>
          <Button
            variant="destructive"
            onClick={() =>
              runAction(
                'Delete',
                () =>
                  deleteKey.mutate(data.key, {
                    onSuccess: () => navigate('/keys'),
                  }),
                `Delete key "${data.key_alias ?? masked}"?`,
              )
            }
          >
            <Trash2 size={16} />
            <span>Delete</span>
          </Button>
        </div>
      </div>

      {regeneratedKey && (
        <Card>
          <div
            style={{
              padding: 12,
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: 6,
              marginBottom: 12,
              color: '#166534',
              fontSize: 13,
            }}
          >
            New key generated. Save it now.
          </div>
          <div
            style={{
              fontFamily: 'monospace',
              fontSize: 13,
              padding: 12,
              background: '#f8fafc',
              borderRadius: 6,
              border: '1px solid #e2e8f0',
              wordBreak: 'break-all',
              display: 'flex',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <span style={{ flex: 1 }}>{regeneratedKey}</span>
            <Button
              size="sm"
              onClick={() => navigator.clipboard.writeText(regeneratedKey)}
            >
              <Copy size={14} />
              <span>Copy</span>
            </Button>
          </div>
        </Card>
      )}

      {actionError && (
        <div
          style={{
            padding: 12,
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 6,
            color: '#991b1b',
            fontSize: 13,
          }}
        >
          {actionError}
        </div>
      )}

      <Card>
        <dl style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12 }}>
          <Row
            label="Key"
            value={
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontFamily: 'monospace' }}>
                  {showKey ? data.key : masked}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowKey(!showKey)}
                >
                  {showKey ? 'Hide' : 'Show'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigator.clipboard.writeText(data.key)}
                >
                  <Copy size={14} />
                </Button>
              </div>
            }
          />
          <Row label="Alias" value={data.key_alias ?? '-'} />
          <Row label="User ID" value={data.user_id ?? '-'} />
          <Row label="Team ID" value={data.team_id ?? '-'} />
          <Row
            label="Models"
            value={data.models?.length ? data.models.join(', ') : 'All'}
          />
          <Row
            label="Spend / Budget"
            value={`$${(data.spend ?? 0).toFixed(2)} / ${data.max_budget != null ? `$${data.max_budget.toFixed(2)}` : '∞'}`}
          />
          <Row
            label="Status"
            value={
              <span style={{ color: data.blocked ? '#dc2626' : '#16a34a' }}>
                {data.blocked ? 'Blocked' : 'Active'}
              </span>
            }
          />
          <Row label="Created" value={data.created_at ?? '-'} />
          <Row label="Expires" value={data.expires ?? '-'} />
        </dl>
      </Card>
    </div>
  );
}

function Row({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <>
      <dt style={{ color: '#64748b', fontSize: 13 }}>{label}</dt>
      <dd style={{ margin: 0, fontSize: 14, color: '#0f172a' }}>{value}</dd>
    </>
  );
}